import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  AcademicCapIcon,
  CogIcon,
  InformationCircleIcon,
  LogoutIcon, 
  UserGroupIcon
} from '@heroicons/react/outline';
import TermsPrivacyModal from '../../../../components/TermsPrivacyModal';
// import { CheckCircleIcon } from '@heroicons/react/solid';
import { signOut } from 'firebase/auth';
import { doc, getDoc, collection, query, where, getDocs, limit } from 'firebase/firestore';
import { auth, db } from '../../../../firebase';
import { PatientSelfDataContext } from '../../../../context/PatientSelfDataContext';
import { ModularPatientSelfDataContext } from '../../../../context/ModularPatientSelfDataContext';
import { ModularInformantSelfDataContext } from '../../../../context/ModularInformantSelfDataContext';
import PatientSettings from './PatientSettings';
import { getPatientFacingTerminology } from '../../../../utils/terminology';

export default function PatientHeader({
  assessments = [],
  onSettingsClick,
  onInformantsClick,
  onSchoolRepClick,
  isInformant = false,
  useModularHeader = false,
}) {
  const navigate = useNavigate();
  const { patientDataModel: adhdPatientData } = useContext(PatientSelfDataContext);
  const { header: modularPatientHeader } = useContext(ModularPatientSelfDataContext);
  const { header: modularInformantHeader, patientFirstName: linkedPatientFirstName } =
    useContext(ModularInformantSelfDataContext);
  const patientDataModel = useModularHeader
    ? (isInformant ? modularInformantHeader : modularPatientHeader)
    : adhdPatientData;
  const user = auth.currentUser;
  
  // Settings states
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [showInformantSetup, setShowInformantSetup] = useState(false);

  // Controls the Terms & Privacy pop-up. Two separate trigger buttons (mobile
  // and desktop) share this single piece of state and one modal instance.
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  
  // User organization state — display name from auth claims (e.g. "The Owl Centre")
  const [userOrganisation, setUserOrganisation] = useState('');
  // Firestore organisations doc ID from auth claims (e.g. "ORG-A1B2C3D4E5"),
  // used to fetch org-level config like branding
  const [orgId, setOrgId] = useState('');
  // Logo URL fetched from the organisation's Firestore document (branding.logoUrl).
  // This replaces the old approach of hardcoding a conditional <img> per provider —
  // orgs now upload their logo via the branding settings, and we read the stored URL.
  const [orgLogoUrl, setOrgLogoUrl] = useState(null);

  // Get the user's first name from the data model
  const firstName = patientDataModel?.firstName || (user?.displayName?.split(' ')[0] || 'User');

  // Linked patient's first name for informant dashboard copy (Autism modular path).
  // ADHD informants use createdByName on their patients/{uid} document instead.
  const informantLinkedPatientName = linkedPatientFirstName
    || patientDataModel?.patientFirstName
    || patientDataModel?.createdByName
    || 'the patient';

  // Extract organisation name and orgId from multiple sources, in priority:
  //   1. Auth custom claims (fastest — no extra read)
  //   2. The patient's Firestore document via patientDataModel (available
  //      immediately after backfill, even if the Auth token hasn't refreshed)
  useEffect(() => {
    const getUserInfo = async () => {
      try {
        if (user) {
          const idTokenResult = await user.getIdTokenResult();
          const claimsOrg = idTokenResult.claims.organisation || '';
          const claimsOrgId = idTokenResult.claims.orgId || '';

          setUserOrganisation(claimsOrg);
          setOrgId(claimsOrgId || patientDataModel?.orgId || '');
        }
      } catch (error) {
        console.error("Error getting user information:", error);
      }
    };

    getUserInfo();
  }, [user, patientDataModel?.orgId]);

  // Fetch the organisation document to get the logo URL.  Uses a layered
  // strategy to handle users whose Auth token may not yet carry orgId:
  //   1. Direct doc read by orgId (ORG-XXXXX) — standard path
  //   2. Query by organisation name — fallback for stale tokens.  Requires
  //      the Firestore rule: `request.auth.token.organisation == resource.data.name`
  useEffect(() => {
    const fetchOrgLogo = async () => {
      try {
        let orgDoc = null;

        if (orgId) {
          const snap = await getDoc(doc(db, 'organisations', orgId));
          if (snap.exists()) orgDoc = snap;
        }

        if (!orgDoc && userOrganisation) {
          const q = query(
            collection(db, 'organisations'),
            where('name', '==', userOrganisation),
            limit(1),
          );
          const snapshot = await getDocs(q);
          if (!snapshot.empty) {
            orgDoc = snapshot.docs[0];
            if (!orgId) setOrgId(orgDoc.id);
          }
        }

        if (orgDoc) {
          const logoUrl = orgDoc.data()?.branding?.logoUrl;
          if (logoUrl) setOrgLogoUrl(logoUrl);
        }
      } catch (error) {
        console.error("Error fetching organisation logo:", error);
      }
    };

    if (orgId || userOrganisation) fetchOrgLogo();
  }, [orgId, userOrganisation]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/service-user/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Determine completion status
  const allAssessmentsComplete = assessments.length > 0 && assessments.every(assessment => assessment.progress === 100);
  const hasCompletedInformantSetup = patientDataModel?.hasCompletedInformantSetupForm;
  const t = getPatientFacingTerminology(userOrganisation);

  return (
    <>
      {/* Compact Header */}
      <header className="relative bg-white" role="banner">
        {/* Gradient Background - More Subtle */}
        <div className="absolute inset-0 bg-gradient-to-b from-violet-100 to-white"></div>
        
        {/* Bottom gradient fade to white */}
        {/* <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-b from-transparent to-white/80"></div> */}
        
        {/* Header Content */}
        <div className="relative px-4 py-4 md:py-6">
          <div className="flex items-center justify-between">
            {/* Logo Section — dynamically loaded from the organisation's branding config */}
            <div className="flex-shrink-0">
              {orgLogoUrl ? (
                <img 
                  src={orgLogoUrl} 
                  alt={`${userOrganisation} Logo`} 
                  className="h-8 md:h-12 w-auto" 
                />
              ) : userOrganisation && (
                <span className="text-lg md:text-xl font-semibold text-gray-800">
                  {userOrganisation}
                </span>
              )}
            </div>

            {/* Action Buttons - Responsive Design */}
            <nav aria-label="Account actions" className="flex items-center gap-2">
              {/* Mobile - Icon Only (min 44px touch targets for WCAG AAA) */}              
              {!isInformant && userOrganisation !== 'ThinkDivergent' && hasCompletedInformantSetup && (
                <button 
                  onClick={onInformantsClick}
                  className="md:hidden min-h-[44px] min-w-[44px] flex items-center justify-center rounded-full bg-white/80 backdrop-blur-sm shadow-sm border border-gray-200 hover:bg-white hover:shadow-md transition-all duration-200"
                  aria-label={`Manage ${t.plural}`}
                >
                  <UserGroupIcon className="w-5 h-5 text-gray-600" />
                </button>
              )}

              {/* CYP guardian: manage the school representative.
                  Mirrors the Adult ADHD "Manage Informants" button placement
                  so every pathway has the same top-row entry point. */}
              {onSchoolRepClick && (
                <button
                  onClick={onSchoolRepClick}
                  className="md:hidden min-h-[44px] min-w-[44px] flex items-center justify-center rounded-full bg-white/80 backdrop-blur-sm shadow-sm border border-gray-200 hover:bg-white hover:shadow-md transition-all duration-200"
                  aria-label="Manage school"
                >
                  <AcademicCapIcon className="w-5 h-5 text-gray-600" />
                </button>
              )}

              {/* Mobile: Terms & Privacy icon-only button (matches Settings / Sign Out pill style) */}
              <button
                onClick={() => setIsTermsOpen(true)}
                className="md:hidden min-h-[44px] min-w-[44px] flex items-center justify-center rounded-full bg-white/80 backdrop-blur-sm shadow-sm border border-gray-200 hover:bg-white hover:shadow-md transition-all duration-200"
                aria-label="Terms and Privacy"
              >
                <InformationCircleIcon className="w-5 h-5 text-gray-600" />
              </button>

              <button 
                onClick={onSettingsClick || (() => setIsSettingsOpen(true))}
                className="md:hidden min-h-[44px] min-w-[44px] flex items-center justify-center rounded-full bg-white/80 backdrop-blur-sm shadow-sm border border-gray-200 hover:bg-white hover:shadow-md transition-all duration-200"
                aria-label="Settings"
              >
                <CogIcon className="w-5 h-5 text-gray-600" />
              </button>
              
              <button 
                onClick={handleLogout}
                className="md:hidden min-h-[44px] min-w-[44px] flex items-center justify-center rounded-full bg-white/80 backdrop-blur-sm shadow-sm border border-gray-200 hover:bg-white hover:shadow-md transition-all duration-200"
                aria-label="Sign Out"
              >
                <LogoutIcon className="w-5 h-5 text-gray-600" />
              </button>

              {/* Desktop - Icon + Text */}             
              {!isInformant && userOrganisation !== 'ThinkDivergent' && hasCompletedInformantSetup && (
                <button 
                  onClick={onInformantsClick}
                  aria-label={`Manage ${t.plural}`}
                  className="hidden md:flex items-center gap-2 px-4 py-2 rounded-lg bg-white/80 backdrop-blur-sm shadow-sm border border-gray-200 hover:bg-white hover:shadow-md transition-all duration-200"
                >
                  <UserGroupIcon className="w-5 h-5 text-gray-600" />
                  <span className="text-sm font-medium text-gray-700">{t.plural}</span>
                </button>
              )}

              {/* CYP guardian: desktop school rep manage button */}
              {onSchoolRepClick && (
                <button
                  onClick={onSchoolRepClick}
                  aria-label="Manage school"
                  className="hidden md:flex items-center gap-2 px-4 py-2 rounded-lg bg-white/80 backdrop-blur-sm shadow-sm border border-gray-200 hover:bg-white hover:shadow-md transition-all duration-200"
                >
                  <AcademicCapIcon className="w-5 h-5 text-gray-600" />
                  <span className="text-sm font-medium text-gray-700">Manage school</span>
                </button>
              )}

              {/* Desktop: Terms & Privacy icon + label button (matches Settings / Sign Out pill style) */}
              <button
                onClick={() => setIsTermsOpen(true)}
                aria-label="Terms and Privacy"
                className="hidden md:flex items-center gap-2 px-4 py-2 rounded-lg bg-white/80 backdrop-blur-sm shadow-sm border border-gray-200 hover:bg-white hover:shadow-md transition-all duration-200"
              >
                <InformationCircleIcon className="w-5 h-5 text-gray-600" />
                <span className="text-sm font-medium text-gray-700">Terms &amp; Privacy</span>
              </button>

              <button 
                onClick={onSettingsClick || (() => setIsSettingsOpen(true))}
                aria-label="Open settings"
                className="hidden md:flex items-center gap-2 px-4 py-2 rounded-lg bg-white/80 backdrop-blur-sm shadow-sm border border-gray-200 hover:bg-white hover:shadow-md transition-all duration-200"
              >
                <CogIcon className="w-5 h-5 text-gray-600" />
                <span className="text-sm font-medium text-gray-700">Settings</span>
              </button>
              
              <button 
                onClick={handleLogout}
                aria-label="Sign out of your account"
                className="hidden md:flex items-center gap-2 px-4 py-2 rounded-lg bg-white/80 backdrop-blur-sm shadow-sm border border-gray-200 hover:bg-white hover:shadow-md transition-all duration-200"
              >
                <LogoutIcon className="w-5 h-5 text-gray-600" />
                <span className="text-sm font-medium text-gray-700">Sign out</span>
              </button>
            </nav>
          </div>

          {/* Welcome Section - More Compact */}
          <div className="mt-4 md:mt-6">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              Welcome, {firstName}
            </h1>

            {/* Status Message - Compact */}
            <div className="text-sm md:text-base text-gray-600 space-y-1">
              {isInformant ? (
                // Informant-specific messages — copy adapts based on whether
                // this is a modular (Autism) or monolithic (ADHD) dashboard
                allAssessmentsComplete ? (
                  <div className="max-w-4xl mx-auto md:px-4">
                    <div className="flex items-start justify-center space-x-2 p-3 mt-2">
                      <div>
                        <p className="text-green-800 font-medium">Thank you for completing all questionnaires!</p>
                        <p className="text-green-700 text-sm mt-1">
                          You've provided valuable insights about {useModularHeader && isInformant ? informantLinkedPatientName : (patientDataModel?.createdByName || 'the patient')}'s {useModularHeader ? 'developmental history' : 'symptoms'} for {userOrganisation}.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <p>
                      {useModularHeader && isInformant
                        ? `You have been invited to help with ${informantLinkedPatientName}'s assessment.`
                        : `You have been invited by ${patientDataModel?.createdByName || 'the patient'} to help with their ${useModularHeader ? 'assessment' : 'ADHD assessment process'}.`}
                    </p>
                    <p className="text-xs md:text-sm text-gray-500 mt-1">
                      {useModularHeader
                        ? `Please answer questions about ${informantLinkedPatientName}'s life by completing the developmental history questionnaires below.`
                        : 'Please complete the questionnaires below to provide your insights about their symptoms.'}
                    </p>
                  </div>
                )
              ) : (
                // Patient-specific messages
                (userOrganisation !== 'ThinkDivergent' && !hasCompletedInformantSetup) ? (
                  <p>Start by setting up your {t.lowerPlural} - people who know you well and can provide additional insights. You'll then be able to access your questionnaires.</p>
                ) : allAssessmentsComplete ? (
                  <div className="max-w-4xl mx-auto md:px-4">
                    <div className="flex items-start justify-center space-x-2 p-3 mt-2">
                      {/* <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" /> */}
                      <div>
                        <p className="text-green-800 font-medium">Congratulations! All questionnaires completed.</p>
                        <p className="text-green-700 text-sm mt-1">
                          You've provided all information needed for {userOrganisation} to review.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <p>Please complete the questionnaires below.</p>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Settings Modal - Only render if not handled externally */}
      {!onSettingsClick && (
        <PatientSettings 
          isOpen={isSettingsOpen}
          onClose={() => setIsSettingsOpen(false)}
          showInformantSetup={showInformantSetup}
          setShowInformantSetup={setShowInformantSetup}
        />
      )}

      {/* Terms & Privacy Modal — single instance shared by mobile and desktop triggers */}
      <TermsPrivacyModal open={isTermsOpen} onOpenChange={setIsTermsOpen} />
    </>
  );
} 